import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt, faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import yaml from 'js-yaml';
import RouteSeo from '../components/RouteSeo';
import ToolContent from '../components/ToolContent';
import ToolLayout from '../components/ToolLayout';
import { JSON_YAML_ABOUT } from '../content/toolGuides';
import { JSON_YAML_ROUTE } from '../content/tools';
import { useTextConverter } from '../hooks/useTextConverter';

const jsonToYaml = (input: string) =>
  yaml.dump(JSON.parse(input), {
    indent: 2,
    lineWidth: -1,
    noRefs: true,
  });

const yamlToJson = (input: string) => JSON.stringify(yaml.load(input), null, 2);

const exampleJson = `{
  "name": "John Doe",
  "age": 30,
  "email": "john@example.com",
  "address": {
    "street": "123 Main St",
    "city": "New York",
    "country": "USA"
  },
  "hobbies": [
    "reading",
    "coding",
    "hiking"
  ]
}`;

const exampleYaml = `name: John Doe
age: 30
email: john@example.com
address:
  street: 123 Main St
  city: New York
  country: USA
hobbies:
  - reading
  - coding
  - hiking`;

const JsonYamlConverter: React.FC = () => {
  const converter = useTextConverter({
    modes: ['jsonToYaml', 'yamlToJson'] as const,
    transforms: { jsonToYaml, yamlToJson },
    errors: {
      jsonToYaml: (err) => `Failed to convert JSON to YAML: ${(err as Error).message}`,
      yamlToJson: (err) => `Failed to convert YAML to JSON: ${(err as Error).message}`,
    },
  });
  const isJsonToYaml = converter.mode === 'jsonToYaml';

  const loadExample = () => converter.reset(isJsonToYaml ? exampleJson : exampleYaml);

  return (
    <>
      <RouteSeo route={JSON_YAML_ROUTE} />
      <ToolLayout
        title="JSON ⇄ YAML Converter"
        description="Convert between JSON and YAML formats"
        icon={faExchangeAlt}
        controls={
          <div className="tool-controls__group">
            <Form.Check
              type="radio"
              id="json-to-yaml-radio"
              label="JSON to YAML"
              checked={isJsonToYaml}
              onChange={() => {
                converter.setMode('jsonToYaml');
                converter.reset();
              }}
              inline
            />
            <Form.Check
              type="radio"
              id="yaml-to-json-radio"
              label="YAML to JSON"
              checked={!isJsonToYaml}
              onChange={() => {
                converter.setMode('yamlToJson');
                converter.reset();
              }}
              inline
            />
            <Button variant="link" size="sm" onClick={loadExample} className="ms-3">
              Load Example
            </Button>
          </div>
        }
        error={converter.error}
        onDismissError={converter.clearError}
        inputLabel={isJsonToYaml ? 'JSON Input' : 'YAML Input'}
        inputValue={converter.input}
        onInputChange={converter.setInput}
        inputPlaceholder={isJsonToYaml ? 'Enter JSON here...' : 'Enter YAML here...'}
        inputRows={15}
        actions={
          <>
            <Button variant="primary" onClick={converter.convert} disabled={!converter.input}>
              <FontAwesomeIcon icon={faArrowRight} className="me-2" />
              Convert
            </Button>
            <Button variant="outline-secondary" onClick={converter.switchMode} disabled={!converter.output}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Reverse
            </Button>
          </>
        }
        outputLabel={isJsonToYaml ? 'YAML Output' : 'JSON Output'}
        outputValue={converter.output}
        outputRows={15}
        onCopy={converter.copy}
        copied={converter.copied}
        aboutTitle="About JSON and YAML"
        about={JSON_YAML_ABOUT}
      >
        <ToolContent route={JSON_YAML_ROUTE} />
      </ToolLayout>
    </>
  );
};

export default JsonYamlConverter;
